import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface GPUProcess {
  pid: number;
  name: string;
  memory: number;
}

interface GPUProcessListProps {
  processes: GPUProcess[];
}

const GPUProcessList: React.FC<GPUProcessListProps> = ({ processes }) => {
  // Sort processes by memory usage
  const sortedProcesses = [...processes].sort((a, b) => b.memory - a.memory).slice(0, 3);

  const formatMemory = (memory: number) => {
    if (memory >= 1024) {
      return `${(memory / 1024).toFixed(1)}GB`;
    }
    return `${memory.toFixed(0)}MB`;
  };

  return (
    <div className="bg-black/50 backdrop-blur-sm p-3 border border-gray-800/50 h-full flex flex-col shadow-lg">
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Processes</h3>
      
      <div className="flex-1 space-y-1 overflow-hidden">
        {sortedProcesses.length > 0 ? (
          <AnimatePresence mode="popLayout">
            {sortedProcesses.map((process, index) => (
              <motion.div
                key={process.pid}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                className="flex items-center justify-between py-1"
              >
                <div className="flex items-center space-x-2 min-w-0">
                  <div className={`h-1.5 w-1.5 ${
                    index === 0 ? 'bg-blue-500/60' : 'bg-gray-600/50'
                  }`} />
                  <span className="text-xs text-gray-300 truncate">
                    {process.name}
                  </span>
                </div>
                <span className="text-xs font-mono text-gray-400 ml-2">
                  {formatMemory(process.memory)}
                </span>
              </motion.div>
            ))}
          </AnimatePresence>
        ) : (
          <div className="flex items-center justify-center h-full">
            <span className="text-xs text-gray-600">No GPU processes</span>
          </div>
        )}
        
        {processes.length > 3 && (
          <div className="pt-1 border-t border-gray-800/50">
            <span className="text-xs text-gray-600">
              +{processes.length - 3} more
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GPUProcessList;